/**
 * IAPWS-97 Fluid Property Adapter for the NS Solver.
 *
 * Converts between NS solver SI units (Pa, J/kg, K) and the iapws-if97
 * library units (MPa, kJ/kg, K). Extracts extended properties (density,
 * speed of sound, viscosity) that the standard FluidProps interface omits.
 *
 * Per CLAUDE.md: "convert at the interface boundary — not inside component logic"
 */

import { solvePT, solvePH, solvePx, solveTx } from 'iapws-if97';
import type { NSFluidProps, Phase } from '../types';

const G = 9.80665; // m/s² standard gravity

/**
 * Look up full fluid properties from pressure and enthalpy (SI units).
 * This is the hot-path function — called once per cell per SIMPLE iteration.
 *
 * @param P_Pa  Pressure in Pa
 * @param h_Jkg Enthalpy in J/kg
 * @returns NSFluidProps in SI units
 */
export function propsFromPH_SI(P_Pa: number, h_Jkg: number): NSFluidProps {
  const P_MPa = P_Pa / 1e6;
  const h_kJkg = h_Jkg / 1e3;

  const state = solvePH(P_MPa, h_kJkg);

  const phase = resolvePhase(state.quality, state.region);

  return {
    P: P_Pa,
    T: state.temperature,    // K (iapws-if97 returns K)
    h: h_Jkg,
    s: state.entropy * 1e3,  // kJ/(kg·K) → J/(kg·K)
    rho: 1 / state.specificVolume,  // m³/kg → kg/m³
    u_int: state.internalEnergy * 1e3,  // kJ/kg → J/kg
    c: state.speedOfSound ?? estimateSpeedOfSound(state, P_MPa),
    mu: estimateViscosity(state.temperature), // BasicProperties lacks viscosity; estimate from T
    cp: (state.cp ?? 4.182) * 1e3,   // kJ/(kg·K) → J/(kg·K)
    cv: (state.cv ?? 3.0) * 1e3,     // kJ/(kg·K) → J/(kg·K)
    x: state.quality ?? (phase === 'subcooled' ? -1 : 2),
    phase: phase as Phase,
  };
}

/**
 * Look up properties from pressure and temperature (SI units).
 */
export function propsFromPT_SI(P_Pa: number, T_K: number): NSFluidProps {
  const P_MPa = P_Pa / 1e6;
  const state = solvePT(P_MPa, T_K);

  const phase = resolvePhase(state.quality, state.region);

  return {
    P: P_Pa,
    T: state.temperature,
    h: state.enthalpy * 1e3,
    s: state.entropy * 1e3,
    rho: 1 / state.specificVolume,
    u_int: state.internalEnergy * 1e3,
    c: state.speedOfSound ?? estimateSpeedOfSound(state, P_MPa),
    mu: state.viscosity ?? estimateViscosity(state.temperature),
    cp: (state.cp ?? 4.182) * 1e3,
    cv: (state.cv ?? 3.0) * 1e3,
    x: state.quality ?? (phase === 'subcooled' ? -1 : 2),
    phase: phase as Phase,
  };
}

/**
 * Get saturation enthalpies at a given pressure.
 * Returns [h_f, h_g] in J/kg.
 */
export function saturationEnthalpies(P_Pa: number): [number, number] {
  const P_MPa = P_Pa / 1e6;
  const liq = solvePx(P_MPa, 0);
  const vap = solvePx(P_MPa, 1);
  return [liq.enthalpy * 1e3, vap.enthalpy * 1e3];
}

/**
 * Get saturation densities at a given pressure.
 * Returns [rho_f, rho_g] in kg/m³.
 */
export function saturationDensities(P_Pa: number): [number, number] {
  const P_MPa = P_Pa / 1e6;
  const liq = solvePx(P_MPa, 0);
  const vap = solvePx(P_MPa, 1);
  return [1 / liq.specificVolume, 1 / vap.specificVolume];
}

/**
 * Saturation (vapour) pressure of the fluid at a given temperature.
 *
 * Uses the IAPWS-IF97 Region-4 saturation line via solveTx(T, x=0): the
 * saturated-liquid state at temperature T, whose pressure IS the saturation
 * pressure p_sat(T). Kept here at the fluid boundary (per CLAUDE.md) so the
 * MPa→Pa conversion never leaks into component logic.
 *
 * Valid range is the IAPWS-IF97 saturation line: 273.16 K ≤ T < 647.096 K
 * (triple point to critical point). Outside this range the saturation concept
 * is undefined; we return null so callers can flag rather than fabricate.
 *
 * @param T_K Temperature in K
 * @returns Saturation pressure in Pa, or null if T is outside the saturation line.
 */
export function vaporPressureFromT_SI(T_K: number): number | null {
  // IAPWS-IF97 Region-4 saturation line bounds (triple point → critical point).
  const T_TRIPLE_K = 273.16;
  const T_CRIT_K = 647.096;
  if (!Number.isFinite(T_K) || T_K < T_TRIPLE_K || T_K >= T_CRIT_K) {
    return null;
  }
  try {
    const sat = solveTx(T_K, 0); // saturated liquid at T → p_sat(T)
    return sat.pressure * 1e6; // MPa → Pa
  } catch {
    return null;
  }
}

/**
 * Saturation (vapour) pressure of the fluid at the temperature implied by a
 * pressure–enthalpy inlet state.
 *
 * The NS network stores junction state as (P, h). To get p_sat at the inlet
 * we first resolve the inlet temperature from (P, h) via solvePH, then look up
 * the saturation line. Returns Pa, or null if the temperature is outside the
 * IAPWS-IF97 saturation range (e.g. supercritical) or the lookup fails.
 *
 * @param P_Pa  Inlet pressure in Pa
 * @param h_Jkg Inlet enthalpy in J/kg
 * @returns Saturation pressure in Pa, or null if undefined / out of range.
 */
export function vaporPressureFromPH_SI(P_Pa: number, h_Jkg: number): number | null {
  try {
    const state = solvePH(P_Pa / 1e6, h_Jkg / 1e3);
    return vaporPressureFromT_SI(state.temperature);
  } catch {
    return null;
  }
}

function resolvePhase(quality: number | null, region: number): string {
  if (quality !== null && quality >= 0 && quality <= 1) return 'two-phase';
  if (region === 1) return 'subcooled';
  if (region === 2 || region === 5) return 'superheated';
  if (region === 3) return 'supercritical';
  return 'subcooled';
}

/** Estimate speed of sound when iapws-if97 returns null (two-phase). */
function estimateSpeedOfSound(state: any, P_MPa: number): number {
  if (state.quality !== null && state.quality >= 0 && state.quality <= 1) {
    // HEM speed of sound in two-phase: much lower than single-phase
    // Simplified: c ≈ 50-200 m/s for wet steam
    const x = state.quality;
    try {
      const liq = solvePx(P_MPa, 0);
      const vap = solvePx(P_MPa, 1);
      const rho_f = 1 / liq.specificVolume;
      const rho_g = 1 / vap.specificVolume;
      const rho_m = 1 / (x / rho_g + (1 - x) / rho_f);
      // Wood's formula for two-phase speed of sound
      const alpha = x * rho_f / (x * rho_f + (1 - x) * rho_g); // void fraction
      const c_f = liq.speedOfSound ?? 1500;
      const c_g = vap.speedOfSound ?? 450;
      const c_m = 1 / Math.sqrt(rho_m * (alpha / (rho_g * c_g * c_g) + (1 - alpha) / (rho_f * c_f * c_f)));
      return Math.max(20, Math.min(c_m, 1500));
    } catch {
      return 100; // fallback for two-phase
    }
  }
  return 1497; // liquid water at 25°C
}

/** Estimate dynamic viscosity when iapws-if97 returns null. */
function estimateViscosity(T_K: number): number {
  // Simplified Andrade equation for water
  // μ(T) ≈ A × exp(B/T), fitted for 273-573 K
  const T = Math.max(273.15, Math.min(T_K, 673.15));
  if (T < 373.15) {
    // Liquid: μ decreases with temperature
    return 2.414e-5 * Math.pow(10, 247.8 / (T - 140));
  } else {
    // Vapour: μ increases with temperature (Sutherland's law)
    return 1.12e-5 * Math.pow(T / 373.15, 0.7);
  }
}
